import React, { useState } from "react";
import { getNotifications, markAllNotificationsRead, createNotification } from "../services/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bell, Check, Loader2, Send } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import TradingBackground from "../components/theme/TradingBackground";
import InlineNotificationCenter from "../components/notifications/InlineNotificationCenter";
import NotificationExamples from "../components/notifications/NotificationExamples";

export default function NotificationDemo() {
    const [activeTab, setActiveTab] = useState("demo");
    const [testMessage, setTestMessage] = useState("");
    const [testType, setTestType] = useState("info");
    const [testTitle, setTestTitle] = useState("");
    const queryClient = useQueryClient();

    const { data: notifications = [], isLoading } = useQuery({
        queryKey: ["notifications"],
        queryFn: async () => {
            const response = await getNotifications();
            return Array.isArray(response.data) ? response.data : (response.data.data || []);
        },
    });

    const sendNotificationMutation = useMutation({
        mutationFn: async (data) => {
            const response = await createNotification({
                ...data,
                is_read: false,
                created_at: new Date().toISOString()
            });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
            setTestMessage("");
            setTestTitle("");
        }
    });

    const markAllReadMutation = useMutation({
        mutationFn: async () => {
            await markAllNotificationsRead();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["notifications"] });
        }
    });

    const handleSendTest = () => {
        if (!testMessage || !testTitle) return;

        sendNotificationMutation.mutate({
            title: testTitle,
            message: testMessage,
            type: testType,
            category: "system"
        });
    };

    const unreadCount = notifications.filter(n => !n.is_read).length;

    return (
        <div className="min-h-screen trading-bg relative p-4 md:p-8">
            <TradingBackground variant="grid" />

            <div className="max-w-6xl mx-auto relative z-10">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold trading-text mb-2">Notifications System</h1>
                        <p className="trading-text-muted">
                            Demonstration of the notification center and examples
                        </p>
                    </div>
                    <div className="relative">
                        <Button variant="outline" className="gap-2">
                            <Bell className="w-4 h-4" />
                            <span className="bg-red-500 text-white text-xs px-1.5 rounded-full">
                                {unreadCount}
                            </span>
                        </Button>
                    </div>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
                    <TabsList>
                        <TabsTrigger value="demo">Interactive Demo</TabsTrigger>
                        <TabsTrigger value="examples">UI Examples</TabsTrigger>
                        <TabsTrigger value="center">Notification Center</TabsTrigger>
                    </TabsList>

                    <TabsContent value="demo" className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* Controls */}
                            <Card className="trading-card">
                                <CardContent className="p-6 space-y-4">
                                    <h3 className="text-lg font-semibold trading-text mb-4">Send Test Notification</h3>

                                    <div className="space-y-2">
                                        <Label>Title</Label>
                                        <Input
                                            value={testTitle}
                                            onChange={(e) => setTestTitle(e.target.value)}
                                            placeholder="Notification title..."
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Message</Label>
                                        <Input
                                            value={testMessage}
                                            onChange={(e) => setTestMessage(e.target.value)}
                                            placeholder="Notification message..."
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <Label>Type</Label>
                                        <Select value={testType} onValueChange={setTestType}>
                                            <SelectTrigger>
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="info">Info</SelectItem>
                                                <SelectItem value="success">Success</SelectItem>
                                                <SelectItem value="warning">Warning</SelectItem>
                                                <SelectItem value="error">Error</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <div className="pt-4 flex gap-2">
                                        <Button
                                            className="flex-1"
                                            onClick={handleSendTest}
                                            disabled={sendNotificationMutation.isPending || !testMessage || !testTitle}
                                        >
                                            {sendNotificationMutation.isPending ? (
                                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                            ) : (
                                                <Send className="w-4 h-4 mr-2" />
                                            )}
                                            Send Notification
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>

                            {/* Live Preview */}
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-lg font-semibold trading-text">Live Preview</h3>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => markAllReadMutation.mutate()}
                                        disabled={unreadCount === 0}
                                    >
                                        <Check className="w-4 h-4 mr-2" />
                                        Mark all read
                                    </Button>
                                </div>

                                <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 min-h-[400px]">
                                    <InlineNotificationCenter />
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="examples">
                        <NotificationExamples />
                    </TabsContent>

                    <TabsContent value="center">
                        <div className="max-w-md mx-auto">
                            <InlineNotificationCenter />
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </div>
    );
}
